import { useState, useEffect, useCallback, useRef } from 'react';
import { BrowseItem, BrowseResponse, BrowseSortBy, browseCatalog } from '../utils/browseApi';
import { logger } from '../utils/logger';
import type { FacetBucket } from '../contexts/SearchContext';

export interface UseBrowseParams {
  sortBy: BrowseSortBy;
  pageSize?: number;
  q?: string;
  ownerAliId?: string;
  ownerId?: string;
  // PR 4b: filter to a specific owned catalog (primary or add-on) by ALI ID.
  // Distinct from ownerAliId (public-browse) and ownerId (delegation) — both
  // of which stay null when the caller switches between their own catalogs.
  catalogAliId?: string;
  ownerOnly?: boolean;
  itemIds?: string[];
  /**
   * GL#24: optional scope union. When present the backend returns own ∪
   * public ∪ delegated results regardless of catalogAliId (the backend
   * guard ignores catalogAliId when scopes are supplied). Supply
   * ['own','public','delegate'] on the authenticated global-search path.
   */
  scopes?: string[];
  /**
   * Facet filter selections (GH#1263/GH#1265) — forwarded verbatim to
   * browseCatalog, which CSV-encodes them under the same param names
   * SearchContext.tsx uses on `/api/search`.
   */
  workType?: string[];
  materials?: string[];
  techniques?: string[];
  creators?: string[];
  decade?: string[];
  /**
   * Facet keys to request aggregations for (e.g. the five standard
   * facets). Only sent on `from === 0` requests — see the `fetchPage`
   * facet-fetch cost comment below.
   *
   * IMPORTANT: like `itemIds` and `scopes`, this array is a dependency of
   * the fetch effect BY IDENTITY — callers must pass a module-level
   * constant or a memoized value (e.g. `ALL_CATALOG_FACETS` from
   * useCatalogFacetFilters), never an inline array literal, or every
   * render triggers a refetch loop.
   */
  facets?: string[];
}

export interface UseBrowseReturn {
  items: BrowseItem[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  loading: boolean;
  error: string | null;
  totalPages: number;
  refetch: () => void;
  removeItems: (ids: string[] | Set<string>) => void;
  /**
   * Facet aggregation buckets from the most recent page-0 fetch that
   * requested them. Null until a `facets`-bearing fetch resolves; retained
   * (not cleared) across pagination since buckets are page-independent.
   */
  facetBuckets: Record<string, FacetBucket[]> | null;
}

const RECENTLY_REMOVED_TTL_MS = 30_000;

export const useBrowse = (params: UseBrowseParams): UseBrowseReturn => {
  const {
    sortBy,
    pageSize = 12,
    q,
    ownerAliId,
    ownerId,
    catalogAliId,
    ownerOnly,
    itemIds,
    scopes,
    workType,
    materials,
    techniques,
    creators,
    decade,
    facets,
  } = params;

  const [items, setItems] = useState<BrowseItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [facetBuckets, setFacetBuckets] = useState<Record<string, FacetBucket[]> | null>(null);

  // Monotonic counter used to discard out-of-order (stale) fetch responses.
  // Each call to fetchPage captures the current value; after awaiting, any
  // response whose id is no longer current is silently discarded.
  const requestIdRef = useRef(0);

  // Track IDs of optimistically removed items so they don't reappear
  // when a refetch returns stale OpenSearch data.
  const recentlyRemovedRef = useRef<Set<string>>(new Set());
  const removeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Ref to track total synchronously for page clamping in removeItems
  const totalRef = useRef(0);

  // Clean up all per-ID timers on unmount
  useEffect(() => {
    return () => {
      removeTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // Track previous params to detect changes that should reset page
  const prevParamsRef = useRef({
    sortBy, pageSize, q, ownerAliId, ownerId, catalogAliId, ownerOnly, itemIds, scopes,
    workType, materials, techniques, creators, decade,
  });

  // Reset to page 0 when filters/sort/pageSize change
  useEffect(() => {
    const prev = prevParamsRef.current;
    const changed =
      prev.sortBy !== sortBy ||
      prev.pageSize !== pageSize ||
      prev.q !== q ||
      prev.ownerAliId !== ownerAliId ||
      prev.ownerId !== ownerId ||
      prev.catalogAliId !== catalogAliId ||
      prev.ownerOnly !== ownerOnly ||
      JSON.stringify(prev.itemIds) !== JSON.stringify(itemIds) ||
      JSON.stringify(prev.scopes) !== JSON.stringify(scopes) ||
      JSON.stringify(prev.workType) !== JSON.stringify(workType) ||
      JSON.stringify(prev.materials) !== JSON.stringify(materials) ||
      JSON.stringify(prev.techniques) !== JSON.stringify(techniques) ||
      JSON.stringify(prev.creators) !== JSON.stringify(creators) ||
      JSON.stringify(prev.decade) !== JSON.stringify(decade);

    if (changed) {
      setPage(0);
      // Mirror SearchContext's synchronous setFacetBuckets(null) on a new
      // search: the previous scope/filter's buckets must not linger while
      // the new page-0 fetch (which re-requests facets) is in flight.
      // Only relevant when the caller opted into facets at all.
      if (facets && facets.length > 0) {
        setFacetBuckets(null);
      }
      prevParamsRef.current = {
        sortBy, pageSize, q, ownerAliId, ownerId, catalogAliId, ownerOnly, itemIds, scopes,
        workType, materials, techniques, creators, decade,
      };
    }
  }, [
    sortBy, pageSize, q, ownerAliId, ownerId, catalogAliId, ownerOnly, itemIds, scopes,
    workType, materials, techniques, creators, decade, facets,
  ]);

  const fetchPage = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    // #1201: a selected-but-empty album passes `itemIds === []` (a DEFINED
    // empty array) — meaning "filter to zero items", so render nothing rather
    // than fall through to the full catalog. browseApi collapses `[]` to "no
    // filter" (`if (itemIds?.length)`), so we must short-circuit here instead
    // of calling it. `undefined` still means "no collection filter" → full
    // catalog. Incrementing requestId above discards any in-flight prior fetch.
    if (itemIds && itemIds.length === 0) {
      setItems([]);
      setTotal(0);
      totalRef.current = 0;
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Facet-fetch cost control: aggregations are a separate DB round-trip
      // per facet (three of which materialize the full matching-id set), so
      // only request them on the page-0 fetch — any filter/scope/query
      // change already resets to page 0 via the effect above, which
      // refreshes buckets. Page>0 requests omit `facets` and the previously
      // returned buckets are retained below (they are page-independent).
      const requestFacets = page === 0 ? facets : undefined;

      const response: BrowseResponse = await browseCatalog({
        q: q || undefined,
        sortBy,
        size: pageSize,
        from: page * pageSize,
        ownerAliId,
        ownerId,
        catalogAliId,
        ownerOnly,
        itemIds,
        scopes,
        workType,
        materials,
        techniques,
        creators,
        decade,
        facets: requestFacets,
      });

      // Discard stale response if a newer fetch has already been issued
      if (requestId !== requestIdRef.current) return;

      const removed = recentlyRemovedRef.current;
      if (removed.size > 0) {
        const filteredItems = response.items.filter(
          item => !removed.has(item.catalogItemId)
        );
        // Use removed.size for total adjustment — handles items on other pages
        const adjustedTotal = Math.max(0, response.total - removed.size);
        setItems(filteredItems);
        setTotal(adjustedTotal);
        totalRef.current = adjustedTotal;
      } else {
        setItems(response.items);
        setTotal(response.total);
        totalRef.current = response.total;
      }

      if (requestFacets && requestFacets.length > 0) {
        setFacetBuckets(response.facets ?? null);
      }
      // else: leave facetBuckets untouched — either not requested on this
      // fetch (page>0) or never requested at all (caller has no rail).
    } catch (err) {
      // Discard stale error if a newer fetch has already been issued
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load catalog items';
      logger.error('Browse fetch failed:', err);
      setError(message);
      setItems([]);
      setTotal(0);
      totalRef.current = 0;
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    sortBy, pageSize, page, q, ownerAliId, ownerId, catalogAliId, ownerOnly, itemIds, scopes,
    workType, materials, techniques, creators, decade, facets,
  ]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const removeItems = useCallback((ids: string[] | Set<string>) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    if (idSet.size === 0) return;

    // Only count genuinely new IDs for total decrement
    const newCount = [...idSet].filter(id => !recentlyRemovedRef.current.has(id)).length;

    // Add to recently removed with per-ID cleanup timers
    idSet.forEach(id => {
      recentlyRemovedRef.current.add(id);
      const existing = removeTimersRef.current.get(id);
      if (existing) clearTimeout(existing);
      removeTimersRef.current.set(id, setTimeout(() => {
        recentlyRemovedRef.current.delete(id);
        removeTimersRef.current.delete(id);
      }, RECENTLY_REMOVED_TTL_MS));
    });

    // Compute new total synchronously via ref for accurate page clamping
    const newTotal = Math.max(0, totalRef.current - newCount);
    totalRef.current = newTotal;
    const newTotalPages = Math.max(1, Math.ceil(newTotal / pageSize));

    setItems(prev => prev.filter(item => !idSet.has(item.catalogItemId)));
    setTotal(newTotal);
    setPage(prev => Math.min(prev, newTotalPages - 1));
  }, [pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items,
    total,
    page,
    setPage,
    loading,
    error,
    totalPages,
    refetch: fetchPage,
    removeItems,
    facetBuckets,
  };
};
